import { MICROS_PER_UNIT } from './Wallet';
import { MoneyMicrosPipe } from 'src/app/shared/pipes/money-micros.pipe';

/**
 * Contrato da configuração global do painel (CU-86akkmf7x):
 * `GET /admin/configurations` e `PUT /admin/configurations/{key}`.
 *
 * O catálogo é uma allowlist em código no backend — o painel edita VALOR, nunca
 * cria nem apaga chave. Cada linha traz o tipo declarado, e é por ele que a tela
 * decide o campo: adivinhar pelo nome da chave transformaria um `_MICROS` no fim
 * do nome em contrato acidental.
 */

export type GlobalConfigurationType =
  | 'boolean'
  | 'int'
  | 'money_micros'
  | 'datetime'
  | 'json'
  | 'string';

/**
 * Confirmação explícita de chave de cobrança (CU-86akkmfhx, C.4).
 *
 * O catálogo marca as chaves que mexem em dinheiro (flags da carteira, faixas,
 * data de corte, preços) e o PUT delas só passa com a confirmação no corpo.
 *
 * Contrato fechado no backend (commit 5f2f43be, CU-86ak77za5-fecha-catalogo):
 * `requires_confirmation` em cada linha do GET e na resposta do PUT, e
 * `{ value, confirmed: true }` no PUT. Sem a confirmação o backend responde 422
 * com `errors.confirmed` e não grava — a tela mostra a mensagem como qualquer
 * 422. Os nomes continuam vivendo SÓ aqui: interface, leitura
 * (`requiresConfirmation`) e corpo (`confirmationPayload`) derivam destas duas
 * constantes.
 */
export const CONFIRMATION_FLAG_FIELD = 'requires_confirmation' as const;
export const CONFIRMATION_BODY_FIELD = 'confirmed' as const;

/** O que a linha do catálogo carrega como valor, já tipado pelo backend. */
export type GlobalConfigurationValue = boolean | number | string | object | null;

export interface GlobalConfiguration {
  key: string;
  /** Assunto na tela: Carteira, Envio V2, Recuperação de saldo, Preços. */
  group: string;
  type: GlobalConfigurationType;
  label: string;
  description: string;

  /** Valor em vigor: o do banco, ou o default do catálogo quando não há linha. */
  value: GlobalConfigurationValue;
  default: GlobalConfigurationValue;

  /**
   * `false` = não existe linha no banco e vale o default do código. A tela
   * mostra a diferença entre "ninguém configurou" e "configuraram com o valor
   * igual ao padrão".
   */
  is_stored: boolean;

  min: number | string | null;
  max: number | string | null;
  nullable: boolean;

  /** Valores aceitos de uma chave de texto (ex.: `RCS_CAPABILITY_PROVIDER`): a tela vira select. */
  options?: string[] | null;

  /**
   * Rota da API que edita a chave quando não é o PUT genérico (front-contrato-r3 §2). Hoje só
   * `WALLET_BONUS_TIERS` (`/admin/wallet/bonus-tiers`): o PUT genérico dela volta sempre 422
   * `edit_route_required`, então a tela mostra o valor sem editor e aponta a tela própria.
   */
  edit_route?: string | null;

  /** Chave de cobrança: salvar exige confirmação explícita (ver acima). */
  [CONFIRMATION_FLAG_FIELD]?: boolean;
}

/** Tela do painel de cada `edit_route` conhecido. */
const EDIT_SCREENS: Record<string, string> = {
  '/admin/wallet/bonus-tiers': '/admin-pricing/bonus-tiers',
  '/admin/menu-new-badges': '/admin-configurations/menu-new-badges',
};

/** A chave é editada noutra tela (`edit_route`), e não pelo PUT genérico. */
export function isEditedElsewhere(configuration: GlobalConfiguration | null | undefined): boolean {
  return !!configuration?.edit_route;
}

/** A tela do painel que edita a chave; `null` se o `edit_route` não tem tela conhecida aqui. */
export function editScreenFor(configuration: GlobalConfiguration | null | undefined): string | null {
  return (configuration?.edit_route && EDIT_SCREENS[configuration.edit_route]) || null;
}

/** A chave pede confirmação antes de salvar. Ausente = não pede. */
export function requiresConfirmation(configuration: GlobalConfiguration | null | undefined): boolean {
  return !!configuration && configuration[CONFIRMATION_FLAG_FIELD] === true;
}

/** O que entra no corpo do PUT de uma chave confirmada. */
export function confirmationPayload(): { [CONFIRMATION_BODY_FIELD]: true } {
  return { [CONFIRMATION_BODY_FIELD]: true };
}

/**
 * Linha da trilha de auditoria (CU-86akkmfav):
 * `GET /admin/configurations/{key}/history`, paginado.
 *
 * `old_value` e `new_value` vêm COMO ESTÃO NA COLUNA — texto normalizado pelo
 * backend ('true'/'false', micros inteiro, ISO8601, JSON serializado) e não o
 * valor tipado do catálogo. Quem formata para leitura é a tela, pelo tipo da
 * chave. `old_value` nulo = a chave não tinha linha e o sistema estava no
 * default do catálogo.
 */
export interface ConfigurationAudit {
  id: string;
  configuration_name: string;
  old_value: string | null;
  new_value: string | null;
  user_id: string | null;
  /** Cópia do e-mail no momento da mudança: a conta pode não existir mais. */
  user_email: string | null;
  ip: string | null;
  user_agent: string | null;
  created_at: string;
  /**
   * A gravação passou pelo passo de confirmação de chave de cobrança
   * (CU-86akkmfhx). Ausente em linhas anteriores ao campo.
   */
  confirmed?: boolean;
}

/** Chaves do mesmo assunto, na ordem em que o catálogo as declara. */
export interface GlobalConfigurationGroup {
  name: string;
  items: GlobalConfiguration[];
}

/**
 * `ConfigurationService` do backend guarda cada chave em cache por 60s, então a
 * mudança salva aqui só vale para todo mundo depois desse intervalo. A tela diz
 * isso em voz alta — quem desliga uma flag de cobrança num incidente precisa
 * saber que o efeito não é instantâneo.
 */
export const CONFIGURATION_PROPAGATION_SECONDS = 60;

/** Casas decimais que os micros comportam: 1.000.000 → 6. */
const MICROS_FRACTION_DIGITS = String(MICROS_PER_UNIT).length - 1;

/** Dinheiro lê como dinheiro: `R$ 1,50`, nunca `R$ 1,5`. */
const MIN_FRACTION_DIGITS = 2;

/**
 * Micros inteiros → o texto editável do campo, em pt-BR ("130.000.000" vira
 * "130,00").
 *
 * Divisão por 1.000.000 em ponto flutuante é o jeito clássico de perder centavo
 * (0,07 volta como 0.06999999999999999), então a conversão é feita recortando a
 * string do inteiro — sem aritmética de float em momento nenhum.
 */
export function microsToAmountInput(micros: number | string | null | undefined): string {
  if (micros === null || micros === undefined || micros === '') {
    return '';
  }

  const amount = Number(micros);

  if (!isFinite(amount)) {
    return '';
  }

  const digits = String(Math.abs(Math.trunc(amount))).padStart(MICROS_FRACTION_DIGITS + 1, '0');
  const whole = digits.slice(0, digits.length - MICROS_FRACTION_DIGITS);
  const fraction = digits.slice(digits.length - MICROS_FRACTION_DIGITS).replace(/0+$/, '');
  const shown = fraction.padEnd(MIN_FRACTION_DIGITS, '0');
  const sign = amount < 0 ? '-' : '';

  return `${sign}${groupThousands(whole)},${shown}`;
}

/**
 * O texto do campo → micros inteiros, para mandar ao backend. Devolve `null`
 * quando não dá para ler o valor como dinheiro — é o que faz o campo recusar o
 * envio em vez de mandar um número inventado.
 *
 * Mais casas do que os micros comportam é INVÁLIDO, não truncado: cortar em
 * silêncio faria o admin salvar um preço diferente do que digitou.
 */
export function amountInputToMicros(text: string | number | null | undefined): number | null {
  if (text === null || text === undefined) {
    return null;
  }

  const raw = String(text).trim();

  if (raw === '') {
    return null;
  }

  const negative = raw.startsWith('-');
  const cleaned = raw.replace(/[^0-9.,]/g, '');

  // A vírgula é o separador decimal do pt-BR; o ponto só vale como decimal
  // quando é o único separador presente (valor colado de outro lugar, "0.13").
  const normalized = cleaned.indexOf(',') >= 0
    ? cleaned.replace(/\./g, '').replace(',', '.')
    : cleaned;

  if (!/^\d+(\.\d+)?$|^\.\d+$|^\d+\.$/.test(normalized)) {
    return null;
  }

  const [whole, fraction = ''] = normalized.split('.');

  if (fraction.length > MICROS_FRACTION_DIGITS) {
    return null;
  }

  const micros = Number((whole || '0') + fraction.padEnd(MICROS_FRACTION_DIGITS, '0'));

  if (!isFinite(micros)) {
    return null;
  }

  return negative ? -micros : micros;
}

/**
 * Mantém no campo apenas o que compõe um valor em dinheiro enquanto se digita:
 * dígitos, uma vírgula só e no máximo as casas que os micros comportam.
 */
export function maskAmountInput(text: string): string {
  const cleaned = String(text ?? '').replace(/[^0-9,]/g, '');
  const firstComma = cleaned.indexOf(',');

  if (firstComma < 0) {
    return cleaned;
  }

  const whole = cleaned.slice(0, firstComma);
  const fraction = cleaned.slice(firstComma + 1).replace(/,/g, '').slice(0, MICROS_FRACTION_DIGITS);

  return `${whole},${fraction}`;
}

function groupThousands(whole: string): string {
  return whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

const moneyPipe = new MoneyMicrosPipe();

/**
 * O valor de uma chave (em vigor, padrão, o novo do modal de confirmação) como texto legível,
 * pelo tipo: dinheiro em R$ (nunca os micros crus — "130000000" lê como 130 milhões), data em
 * `dd/MM/yyyy HH:mm`.
 */
export function describeConfigurationValue(
  configuration: GlobalConfiguration,
  value: GlobalConfigurationValue
): string {
  if (value === null || value === undefined || value === '') {
    return configuration.type === 'boolean' ? 'Desligado' : 'Vazio';
  }

  switch (configuration.type) {
    case 'boolean':
      return value === true || value === 'true' ? 'Ligado' : 'Desligado';

    case 'json':
      return typeof value === 'string' ? value : JSON.stringify(value);

    case 'datetime':
      return datetimeLabel(String(value));

    case 'money_micros':
      return isFinite(Number(value)) ? moneyPipe.transform(value as number | string) : String(value);

    default:
      return String(value);
  }
}

/** ISO → `dd/MM/yyyy HH:mm` no fuso do navegador (o mesmo do campo `datetime-local`). */
export function datetimeLabel(iso: string): string {
  const date = new Date(iso);

  if (isNaN(date.getTime())) {
    return iso;
  }

  const pad = (part: number) => String(part).padStart(2, '0');

  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} `
    + `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Status 0 ou 5xx na gravação de uma chave: não se sabe se o valor entrou. */
export function configurationOutcomeUnknownMessage(configuration: GlobalConfiguration): string {
  return `Não deu para confirmar se ${configuration.label} foi salva. `
    + 'Recarregue para ver o valor em vigor antes de salvar de novo.';
}
